import { sagaIntl } from '@i18n';
import { get, omit, sortBy, uniqBy } from 'lodash';
import { CaseReducer, createReducer } from '@reduxjs/toolkit';

import { GetActionCreatorPayloadT, notNil } from '@utils';
import * as actions from '../actions';
import {
  AudioEditSlice,
  AudioEditDetails,
  TranscriptionViewableWords,
} from '../models';
import { defaultState } from '../store';
import { produce, produceWithPatches } from 'immer';
import { markTdoChanged } from './tdoChanged';

type Re<P = unknown> = CaseReducer<
  typeof defaultState,
  { payload: P; type: string }
>;

const onSelectWords: Re<actions.SelectWordsRequest> = (state, { payload }) => ({
  ...state,
  transcriptionView: {
    ...state.transcriptionView,
    selected: uniqBy(
      [...state.transcriptionView.selected, ...payload],
      'id'
    ).sort((a, b) => a.startTimeMs - b.stopTimeMs),
  },
});

/**
 * Overlap, not containment: a drag that starts or ends mid-word still takes
 * that word. Strict on both sides so a word merely touching the boundary is
 * not swept in — except for a zero-duration series, which has no width to
 * overlap with and would otherwise be unselectable at the edges of a range
 * that is itself built from word timings.
 */
const intersectsRange = (
  word: { startTimeMs: number; stopTimeMs: number },
  rangeStart: number,
  rangeEnd: number
) =>
  word.startTimeMs === word.stopTimeMs
    ? word.startTimeMs >= rangeStart && word.startTimeMs <= rangeEnd
    : word.startTimeMs < rangeEnd && word.stopTimeMs > rangeStart;

const onSelectRangeWords: Re<actions.SelectRangeWordsRequest> = (
  state,
  { payload }
) => {
  const [{ startTimeMs: rangeStart }, { stopTimeMs: rangeEnd }] = sortBy(
    payload,
    'startTimeMs'
  ) as typeof payload; // Work around sortBy not being typed well for tuples

  const selected = (state.transcription?.transcription || [])
    .filter((t) => intersectsRange(t, rangeStart, rangeEnd))
    .sort((a, b) => a.startTimeMs - b.startTimeMs);

  // skip leading punctuation with start same time as first actual word
  if (
    selected.length > 1 &&
    selected[0]!.startTimeMs === selected[1]!.startTimeMs
  ) {
    selected.shift();
  }

  return {
    ...state,
    transcriptionView: {
      ...state.transcriptionView,
      selected,
    },
  };
};

const onDeselectWords: Re<actions.DeselectWordsRequest> = (
  state,
  { payload }
) => ({
  ...state,
  transcriptionView: {
    ...state.transcriptionView,
    selected: state.transcriptionView.selected.filter(
      (s) => !payload.includes(s)
    ),
  },
});

const onDeselectAll: Re<actions.DeselectAllRequest> = (state) => ({
  ...state,
  transcriptionView: {
    ...state.transcriptionView,
    selected: [],
  },
});
export const onMaskVoiceWords: Re<actions.MaskVoiceWordsRequest> = (
  state,
  { payload }
) => {
  const intl = sagaIntl();
  const [newState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      draft.transcriptionView.voiceMaskEdits = normalizeAudioSlices(
        draft.transcriptionView.voiceMaskEdits,
        payload.map<AudioEditSlice>(({ startTimeMs, stopTimeMs, words }) => [
          startTimeMs,
          stopTimeMs,
          {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            user: get(draft, 'user.email', 'Unknown'),
            modifiedDateTime: new Date().toISOString(),
            labels: [],
            notes: words,
            hasAuditEvent: true,
            voiceMask: true,
          },
        ])
      );
      draft.transcriptionView.audioRedactionEdits = subtractAudioSlices(
        draft.transcriptionView.audioRedactionEdits,
        payload.map<AudioEditSlice>(({ startTimeMs, stopTimeMs }) => [
          startTimeMs,
          stopTimeMs,
          null,
        ])
      );
    }
  );

  return produce(newState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'maskVoiceWords',
          defaultMessage: 'voice mask words',
        }),
      },
    });
    draft.history.future = [];
  });
};

export const onRedactWords: Re<actions.RedactWordsRequest> = (
  state,
  { payload }
) => {
  const intl = sagaIntl();
  const { words, redactAll } = payload;
  const [newState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      draft.transcriptionView.audioRedactionEdits = normalizeAudioSlices(
        draft.transcriptionView.audioRedactionEdits,
        words.map<AudioEditSlice>(({ startTimeMs, stopTimeMs, words }) => [
          startTimeMs,
          stopTimeMs,
          {
            // TODO: type things properly
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            user: get(draft, 'user.email', 'Unknown'),
            modifiedDateTime: new Date().toISOString(),
            labels: [],
            notes: words,
            hasAuditEvent: true,
          },
        ])
      );
      draft.transcriptionView.voiceMaskEdits = subtractAudioSlices(
        draft.transcriptionView.voiceMaskEdits,
        words.map<AudioEditSlice>(({ startTimeMs, stopTimeMs }) => [
          startTimeMs,
          stopTimeMs,
          null,
        ])
      );
    }
  );

  return produce(newState, (draft) => {
    const description = redactAll
      ? intl.formatMessage({
          id: 'redactAllWords',
          defaultMessage: 'redact all words',
        })
      : intl.formatMessage({
          id: 'redactWords',
          defaultMessage: 'redact words',
        });

    draft.history.past.push({
      patches,
      inversePatches,
      metadata: { description },
    });
    draft.history.future = [];
  });
};

export const onResetWords: Re<actions.ResetWordsRequest> = (
  state,
  { payload }
) => {
  const intl = sagaIntl();
  const { words, resetAll } = payload;
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);

      draft.transcriptionView.audioRedactionEdits = subtractAudioSlices(
        draft.transcriptionView.audioRedactionEdits,
        words.map<AudioEditSlice>(({ startTimeMs, stopTimeMs }) => [
          startTimeMs,
          stopTimeMs,
          null,
        ])
      );

      draft.transcriptionView.voiceMaskEdits = subtractAudioSlices(
        draft.transcriptionView.voiceMaskEdits,
        words.map<AudioEditSlice>(({ startTimeMs, stopTimeMs }) => [
          startTimeMs,
          stopTimeMs,
          null,
        ])
      );
    }
  );

  return produce(nextState, (draft) => {
    const description = resetAll
      ? intl.formatMessage({
          id: 'resetAllWords',
          defaultMessage: 'reset all words',
        })
      : intl.formatMessage({
          id: 'resetWords',
          defaultMessage: 'reset words',
        });
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description,
      },
    });
    draft.history.future = [];
  });
};

const onRedactSlice: Re<actions.RedactSliceRequest> = (state, { payload }) => {
  const intl = sagaIntl();
  const { redact, reset } = payload;
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      if (reset) {
        draft.transcriptionView.audioRedactionEdits = subtractAudioSlices(
          draft.transcriptionView.audioRedactionEdits,
          reset
        );
      }
      draft.transcriptionView.audioRedactionEdits = normalizeAudioSlices(
        draft.transcriptionView.audioRedactionEdits,
        redact.map<AudioEditSlice>(([startTimeMs, stopTimeMs]) => [
          startTimeMs,
          stopTimeMs,
          {
            // TODO: type things properly
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            user: get(draft, 'user.email', 'Unknown'),
            modifiedDateTime: new Date().toISOString(),
            labels: [],
            notes: 'Timeline redaction',
            hasAuditEvent: true,
          },
        ])
      );
      draft.transcriptionView.voiceMaskEdits = subtractAudioSlices(
        draft.transcriptionView.voiceMaskEdits,
        redact
      );
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'addAudioRedactionSlice',
          defaultMessage: 'add audio redaction slice',
        }),
      },
    });
    draft.history.future = [];
  });
};

const onMaskVoiceSlice: Re<actions.MaskSliceRequest> = (state, { payload }) => {
  const intl = sagaIntl();
  const { mask, reset } = payload;
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      if (reset) {
        draft.transcriptionView.voiceMaskEdits = subtractAudioSlices(
          draft.transcriptionView.voiceMaskEdits,
          reset
        );
      }
      draft.transcriptionView.voiceMaskEdits = normalizeAudioSlices(
        draft.transcriptionView.voiceMaskEdits,
        mask.map<AudioEditSlice>(([startTimeMs, stopTimeMs]) => [
          startTimeMs,
          stopTimeMs,
          {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            user: get(draft, 'user.email', 'Unknown'),
            modifiedDateTime: new Date().toISOString(),
            labels: [],
            notes: 'Timeline voice mask',
            hasAuditEvent: true,
            voiceMask: true,
          },
        ])
      );
      draft.transcriptionView.audioRedactionEdits = subtractAudioSlices(
        draft.transcriptionView.audioRedactionEdits,
        mask
      );
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'addVoiceMaskSlice',
          defaultMessage: 'add voice mask slice',
        }),
      },
    });
    draft.history.future = [];
  });
};

const onSelectSlice: Re<actions.SelectSliceRequest> = (state, { payload }) => {
  if (!state.transcription) {
    return state;
  }

  return {
    ...state,
    transcriptionView: {
      ...state.transcriptionView,
      selected: [
        ...selectTransciptAlignedWordsFromAudioEditSlice(
          payload,
          state.transcription.transcription
        ),
      ],
    },
  };
};

const onResetSlice: Re<actions.ResetSliceRequest> = (state, { payload }) => {
  const intl = sagaIntl();

  const containsVoiceMask = payload.some(([start, end]) =>
    state.transcriptionView.voiceMaskEdits.some(
      ([s, e, details]) => s < end && e > start && details?.voiceMask
    )
  );

  const containsAudioRedaction = payload.some(([start, end]) =>
    state.transcriptionView.audioRedactionEdits.some(
      ([s, e, details]) => s < end && e > start && details
    )
  );

  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);

      // currently we have separate lists for audio redaction and audio voice mask
      draft.transcriptionView.audioRedactionEdits = subtractAudioSlices(
        draft.transcriptionView.audioRedactionEdits,
        payload
      );
      draft.transcriptionView.voiceMaskEdits = subtractAudioSlices(
        draft.transcriptionView.voiceMaskEdits,
        payload
      );
    }
  );

  let description: string;
  if (containsVoiceMask && containsAudioRedaction) {
    description = intl.formatMessage({
      id: 'removeAudioEditSlice',
      defaultMessage: 'remove audio edit slice',
    });
  } else if (containsVoiceMask) {
    description = intl.formatMessage({
      id: 'removeVoiceMaskSlice',
      defaultMessage: 'remove voice mask slice',
    });
  } else {
    description = intl.formatMessage({
      id: 'removeAudioRedactionSlice',
      defaultMessage: 'remove audio redaction slice',
    });
  }

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: { description },
    });
    draft.history.future = [];
  });
};

const onRemoveAllAudioRedactions: Re<
  actions.RemoveAllAudioRedactionsRequest
> = (state) => {
  const intl = sagaIntl();
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      draft.transcriptionView.audioRedactionEdits = [];
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'removeAllAudioRedactionsHistory',
          defaultMessage: 'remove all audio redactions',
        }),
      },
    });
    draft.history.future = [];
  });
};

const onRemoveAllVoiceMaskEdits: Re<actions.RemoveAllVoiceMaskEditsRequest> = (
  state
) => {
  const intl = sagaIntl();
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      draft.transcriptionView.voiceMaskEdits = [];
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'removeAllVoiceMaskHistory',
          defaultMessage: 'remove all voice mask',
        }),
      },
    });
    draft.history.future = [];
  });
};

// Drops the grouping from the merged view so the flat transcript shows at once.
// Deliberately no markTdoChanged: the assets are already gone server-side and
// no pending edit changed, so this must not leave the TDO looking unsaved.
const onApplyDeleteSpeakerSeparation: Re<undefined> = (state) => {
  const intl = sagaIntl();
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      if (draft.transcription) {
        draft.transcription.speakerSegments = undefined;
        // The ids go with the segments. They name assets that no longer exist,
        // and they are what the re-run guard asks "has separation already run"
        // — leaving them behind made deleting the results no way back to
        // running them again, which is the exact failure the guard was changed
        // to fix (review B5). Cleared inside the same patch so undo restores
        // the pair together.
        draft.transcription.speakerSeparationAssetIds = undefined;
      }
    }
  );

  return produce(nextState, (draft) => {
    // Labelled like its sibling above: the History modal and the undo tooltip
    // read this store, so an entry without a description renders blank. The
    // saga builds the same string but hands it only to the worker store, which
    // nothing displays.
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'deleteSpeakerSeparationHistory',
          defaultMessage: 'delete speaker separation',
        }),
      },
    });
    draft.history.future = [];
  });
};

const onApplyDeleteTranscription: Re<
  GetActionCreatorPayloadT<typeof actions.APPLY_DELETE_TRANSCRIPTION>
> = (state, { payload: { removeAudioRedactions, removeVoiceMask } }) => {
  const intl = sagaIntl();
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      draft.transcription = undefined;
      draft.progress.transcription = 1;
      if (removeAudioRedactions) {
        draft.transcriptionView.audioRedactionEdits = [];
      }
      if (removeVoiceMask) {
        draft.transcriptionView.voiceMaskEdits = [];
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'deleteTranscriptionHistory',
          defaultMessage: 'delete transcription',
        }),
      },
    });
    draft.history.future = [];
  });
};

// VE-27765: opening and closing the audio notes / buffer modals is view-only —
// these reducers touch `transcriptionView` (which modal is open, which slice is
// selected) and nothing that is ever saved. They used to mark the editor dirty,
// which was invisible while any landing save cleared the flag outright: the
// Save button lit up for a moment and the next save turned it off. A dirty mark
// now also bumps `saveRev`, and a bump that no in-flight save can account for
// keeps the editor unsaved until the user saves again — so merely opening a
// notes modal during an upload would leave Save lit and raise the leave prompt
// with nothing pending. The real mutations (UPDATE_/DELETE_ NOTES and BUFFER)
// mark dirty themselves, so nothing is lost by dropping it here.
const onViewBuffer: Re<actions.ViewBufferRequest> = (
  state,
  { payload: selectedAudioEditWord }
) => ({
  ...state,
  transcriptionView: {
    ...state.transcriptionView,
    selectedAudioEditWord,
    editBufferOpen: true, // make the modal open
  },
});
const onViewNotes: Re<actions.ViewNotesRequest> = (
  state,
  { payload: [s, e] }
) => {
  let selectedAudioEditSlice = state.transcriptionView.audioRedactionEdits.find(
    (r) => s >= r[0] && e <= r[1]
  );

  // if not found search voiceMaskEdits
  if (!selectedAudioEditSlice) {
    selectedAudioEditSlice = state.transcriptionView.voiceMaskEdits.find(
      (r) => s >= r[0] && e <= r[1]
    );
  }

  return {
    ...state,
    transcriptionView: {
      ...state.transcriptionView,
      selectedAudioEditSlice,
      editNotesOpen: true, // make the modal open
    },
  };
};

// Bound to both CLOSE_NOTES and CLOSE_BUFFER. View-only; see onViewBuffer.
const onCloseEditModal: Re<void> = (state) => ({
  ...state,
  transcriptionView: {
    ...state.transcriptionView,
    selectedAudioEditSlice: undefined,
    editNotesOpen: false, // close the modal
    editBufferOpen: false, // close the modal
    currentEditWords: undefined,
  },
});

const onUpdateNotes: Re<actions.UpdateNotesRequest> = (
  state,
  { payload: [s, e, n] }
) => {
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      const isVoiceMask =
        draft.transcriptionView.selectedAudioEditSlice?.[2]?.voiceMask;
      if (isVoiceMask) {
        draft.transcriptionView.voiceMaskEdits =
          draft.transcriptionView.voiceMaskEdits.map((r) =>
            s >= r[0] && e <= r[1]
              ? [
                  r[0],
                  r[1],
                  {
                    ...r[2],
                    ...n,
                    modifiedDateTime: new Date().toISOString(),
                    // TODO: type things properly
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    user: get(state, 'user.email', 'Unknown'),
                  },
                ]
              : r
          );
      } else {
        draft.transcriptionView.audioRedactionEdits =
          draft.transcriptionView.audioRedactionEdits.map((r) =>
            s >= r[0] && e <= r[1]
              ? [
                  r[0],
                  r[1],
                  {
                    ...r[2],
                    ...n,
                    modifiedDateTime: new Date().toISOString(),
                    // TODO: type things properly
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    user: get(state, 'user.email', 'Unknown'),
                  },
                ]
              : r
          );
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.transcriptionView.selectedAudioEditSlice = undefined;
    draft.transcriptionView.editNotesOpen = false; // close modal
    draft.transcriptionView.currentEditWords = undefined;
    draft.history.past.push({
      patches,
      inversePatches,
      // TODO: distinguish between add/update
      metadata: { description: 'add/update audio redaction note' },
    });
    draft.history.future = [];
  });
};

export const onDeleteNotes: Re<actions.DeleteNotesRequest> = (
  state,
  { payload: [s, e] }
) => {
  const intl = sagaIntl();
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      draft.transcriptionView.audioRedactionEdits =
        draft.transcriptionView.audioRedactionEdits.map((r) =>
          s >= r[0] && e <= r[1]
            ? [
                r[0],
                r[1],
                {
                  ...r[2],
                  modifiedDateTime: new Date().toISOString(),
                  // TODO: type things properly
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  user: get(state, 'user.email', 'Unknown'),
                  notes: '',
                  labels: [],
                },
              ]
            : r
        );

      draft.transcriptionView.voiceMaskEdits =
        draft.transcriptionView.voiceMaskEdits.map((r) =>
          s >= r[0] && e <= r[1]
            ? [
                r[0],
                r[1],
                {
                  ...r[2],
                  modifiedDateTime: new Date().toISOString(),
                  // TODO: type things properly
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  user: get(state, 'user.email', 'Unknown'),
                  notes: '',
                  labels: [],
                },
              ]
            : r
        );
      draft.selectedAudioEditSlice = undefined;
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'deleteAudioRedactionNote',
          defaultMessage: 'delete audio redaction note',
        }),
      },
    });
    draft.history.future = [];
  });
};

const onUpdateAudioEditSpan: Re<actions.UpdateAudioEditRequest> = (
  state,
  {
    payload: {
      oldSlice: [s, e],
      newSlice,
    },
  }
) => {
  const intl = sagaIntl();
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      const isVoiceMask =
        draft.transcriptionView.selectedAudioEditWord?.editDetails.voiceMask;
      if (isVoiceMask) {
        // find original edit and adjust range

        draft.transcriptionView.voiceMaskEdits =
          draft.transcriptionView.voiceMaskEdits.map((r) =>
            s >= r[0] && e <= r[1]
              ? [
                  newSlice[0],
                  newSlice[1],
                  {
                    ...r[2],
                    ...newSlice[2],
                    modifiedDateTime: new Date().toISOString(),
                    // TODO: type things properly
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    user: get(state, 'user.email', 'Unknown'),
                  },
                ]
              : r
          );
      } else {
        // find original edit and adjust range
        draft.transcriptionView.audioRedactionEdits =
          draft.transcriptionView.audioRedactionEdits.map((r) =>
            s >= r[0] && e <= r[1]
              ? [
                  newSlice[0],
                  newSlice[1],
                  {
                    ...r[2],
                    ...newSlice[2],
                    modifiedDateTime: new Date().toISOString(),
                    // TODO: type things properly
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    user: get(state, 'user.email', 'Unknown'),
                  },
                ]
              : r
          );
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.transcriptionView.selectedAudioEditSlice = undefined;
    draft.transcriptionView.editBufferOpen = false; // close modal
    draft.transcriptionView.currentEditWords = undefined;
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'updateAudioBuffer',
          defaultMessage: 'update audio buffer',
        }),
      },
    });
    draft.history.future = [];
  });
};

export const onDeleteBuffer: Re<actions.DeleteBufferRequest> = (
  state,
  { payload: [s, e, d] }
) => {
  const intl = sagaIntl();
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      draft.transcriptionView.audioRedactionEdits =
        draft.transcriptionView.audioRedactionEdits.map((r) =>
          s >= r[0] && e <= r[1]
            ? [
                r[0] + (d.startBufferMs || 0), // remove buffer
                r[1] - (d.stopBufferMs || 0), // remove buffer,
                {
                  ...r[2],
                  modifiedDateTime: new Date().toISOString(),
                  // TODO: type things properly
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  user: get(state, 'user.email', 'Unknown'),
                  startBufferMs: 0,
                  stopBufferMs: 0,
                  labels: r[2]?.labels || [],
                  notes: r[2]?.notes || '',
                },
              ]
            : r
        );

      draft.transcriptionView.voiceMaskEdits =
        draft.transcriptionView.voiceMaskEdits.map((r) =>
          s >= r[0] && e <= r[1]
            ? [
                r[0] + (d.startBufferMs || 0), // reverse buffer
                r[1] - (d.stopBufferMs || 0), // reverse buffer,
                {
                  ...r[2],
                  modifiedDateTime: new Date().toISOString(),
                  // TODO: type things properly
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  user: get(state, 'user.email', 'Unknown'),
                  startBufferMs: 0,
                  stopBufferMs: 0,
                  labels: r[2]?.labels || [],
                  notes: r[2]?.notes || '',
                },
              ]
            : r
        );
      draft.selectedAudioEditSlice = undefined;
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'deleteAudioBuffer',
          defaultMessage: 'delete audio buffer',
        }),
      },
    });
    draft.history.future = [];
  });
};

export const onAddRedactionCodeToTranscription: Re<
  actions.AddRedactionCodeToTranscriptionRequest
> = (state, { payload }) => {
  const intl = sagaIntl();
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      draft.transcriptionView.audioRedactionEdits = insertRedactionCode(
        draft.transcriptionView.audioRedactionEdits,
        payload,
        get(draft, 'user.email', 'Unknown')
      );
      draft.transcriptionView.voiceMaskEdits = insertRedactionCode(
        draft.transcriptionView.voiceMaskEdits,
        payload,
        get(draft, 'user.email', 'Unknown')
      );
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'addRedactionCodeToTranscription',
          defaultMessage: 'add redaction code to transcription',
        }),
      },
    });
    draft.history.future = [];
  });
};

export const onRemoveRedactionCodeFromTranscription: Re<
  actions.RemoveRedactionCodeFromTranscriptionRequest
> = (state, { payload }) => {
  const intl = sagaIntl();
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      markTdoChanged(draft);
      draft.transcriptionView.audioRedactionEdits = removeRedactionCode(
        state.transcriptionView.audioRedactionEdits,
        payload
      );
      draft.transcriptionView.voiceMaskEdits = removeRedactionCode(
        state.transcriptionView.voiceMaskEdits,
        payload
      );
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: {
        description: intl.formatMessage({
          id: 'removeRedactionCodeFromTranscription',
          defaultMessage: 'remove redaction code from transcription',
        }),
      },
    });
    draft.history.future = [];
  });
};

// View-only; see onViewBuffer.
const setCurrentWordsForNotes: Re<string> = (state, { payload }) => ({
  ...state,
  transcriptionView: {
    ...state.transcriptionView,
    currentEditWords: payload,
  },
});

export default createReducer(defaultState, (builder) => {
  builder
    .addCase(actions.SELECT_WORDS, onSelectWords)
    .addCase(actions.SELECT_RANGE_WORDS, onSelectRangeWords)
    .addCase(actions.DESELECT_WORDS, onDeselectWords)
    .addCase(actions.DESELECT_ALL, onDeselectAll)
    .addCase(actions.MASK_VOICE_WORDS, onMaskVoiceWords)
    .addCase(actions.REDACT_WORDS, onRedactWords)
    .addCase(actions.RESET_WORDS, onResetWords)
    .addCase(actions.REDACT_SLICE, onRedactSlice)
    .addCase(actions.SELECT_SLICE, onSelectSlice)
    .addCase(actions.RESET_SLICE, onResetSlice)
    .addCase(actions.MASK_SLICE, onMaskVoiceSlice)
    .addCase(actions.REMOVE_ALL_AUDIO_REDACTIONS, onRemoveAllAudioRedactions)
    .addCase(actions.REMOVE_ALL_VOICE_MASK_EDITS, onRemoveAllVoiceMaskEdits)
    .addCase(actions.APPLY_DELETE_TRANSCRIPTION, onApplyDeleteTranscription)
    .addCase(
      actions.APPLY_DELETE_SPEAKER_SEPARATION,
      onApplyDeleteSpeakerSeparation
    )
    .addCase(actions.VIEW_NOTES, onViewNotes)
    .addCase(actions.CLOSE_NOTES, onCloseEditModal)
    .addCase(actions.UPDATE_NOTES, onUpdateNotes)
    .addCase(actions.DELETE_NOTES, onDeleteNotes)
    .addCase(actions.VIEW_BUFFER, onViewBuffer)
    .addCase(actions.CLOSE_BUFFER, onCloseEditModal)
    .addCase(actions.UPDATE_BUFFER, onUpdateAudioEditSpan)
    .addCase(actions.DELETE_BUFFER, onDeleteBuffer)
    .addCase(
      actions.ADD_REDACTION_CODE_TO_TRANSCRIPTION,
      onAddRedactionCodeToTranscription
    )
    .addCase(
      actions.REMOVE_REDACTION_CODE_FROM_TRANSCRIPTION,
      onRemoveRedactionCodeFromTranscription
    )
    .addCase(actions.SET_CURRENT_WORDS_FOR_NOTES, setCurrentWordsForNotes);
});

/**
 * A redaction slice or gap cannot be less than in MS.
 */
const SLICE_MIN_MS = 1; // 33; // might be able to entirely remove this

/**
 * Takes a list of redacted slices and merges overlapping times.
 * Note: Slices are inclusive.
 */
const normalizeAudioSlices = (
  slices: Array<AudioEditSlice>,
  newSlices: Array<AudioEditSlice> = []
): Array<AudioEditSlice> => {
  const sortedSlices = sortBy(slices.concat(newSlices), '0');
  if (sortedSlices.length < 2) {
    return sortedSlices;
  }
  // const normalized: AudioEditSlice[] = [];
  let start = Infinity;
  let end = 0;
  let notes: AudioEditSlice[2];
  const normalized = sortedSlices.reduce<AudioEditSlice[]>(
    (acc, slice, i, arr) => {
      start = Math.min(start, slice[0]);
      end = Math.max(end, slice[1]);
      notes = mergeNotes([notes, slice[2]]);
      const nextSlice = arr[i + 1];
      if (nextSlice && nextSlice[0] >= end + SLICE_MIN_MS) {
        // if there is a gap before next
        if (end - start > SLICE_MIN_MS) {
          acc.push([start, end, notes]);
          notes = undefined;
        }
        start = Infinity;
        end = 0;
      }
      return acc;
    },
    []
  );
  normalized.push([start, end, notes]);
  return normalized;
};

/**
 * convert audio edit slice into array of transcript viewable words - align start and stop time to word boundaries
 */
const selectTransciptAlignedWordsFromAudioEditSlice = (
  range: AudioEditSlice,
  transcriptionText: Array<TranscriptionViewableWords> = []
): Array<TranscriptionViewableWords> => {
  const startTimeMs = range[0];
  const stopTimeMs = range[1];
  const finalSelected: Array<TranscriptionViewableWords> = [];

  // find all words that overlap range
  transcriptionText.forEach((w) => {
    if (intersectsRange(w, startTimeMs, stopTimeMs)) {
      finalSelected.push(w);
    }
  });

  // if start/end is spacer (no transribed words found) then adjust then use the start/end of original span to avoid select entire span of no words
  if (finalSelected[0]?.isSpacer) {
    finalSelected[0] = { ...finalSelected[0], startTimeMs };
  }

  const end = finalSelected.length - 1;
  if (finalSelected[end]?.isSpacer) {
    finalSelected[end] = { ...finalSelected[end], stopTimeMs };
  }

  return finalSelected;
};

const subtractAudioSlices = (
  initSlices: Array<AudioEditSlice>,
  initCutSlices: Array<AudioEditSlice>
): Array<AudioEditSlice> => {
  /** remove cut slices from initSlices */
  const slices = sortBy(initSlices, '0');
  const cutSlices = normalizeAudioSlices(initCutSlices);
  const newSlices: AudioEditSlice[] = [];
  for (const slice of slices) {
    let [start] = slice;
    const [, end, notes] = slice;
    for (const [cs, ce, cn] of cutSlices) {
      // cut after current slice - break
      if (cs >= end) {
        break;
      }
      // cut before current slice - continue to next cut
      if (ce < start) {
        continue;
      }

      // must have overlap

      // inside current slice - create new slice to the left
      if (cs > start) {
        // create new slice to the left
        newSlices.push([start, cs, mergeNotes([notes, cn])]); // new slice up to cut
      }

      start = ce;
    }

    // add remainder of slice
    if (start < end) {
      newSlices.push([start, end, notes]);
    }
  }
  return normalizeAudioSlices(newSlices);
};

const mergeNotes = (
  notes: Array<AudioEditDetails | null | undefined>
): AudioEditDetails | undefined => {
  const ns = notes.filter(notNil);
  if (ns.length === 0) {
    return;
  }

  return ns.reduce((note, n) => ({
    ...note,
    modifiedDateTime:
      n.modifiedDateTime.localeCompare(note.modifiedDateTime) === -1
        ? note.modifiedDateTime
        : n.modifiedDateTime,
    labels: [...note.labels, ...n.labels],
    notes: `${note.notes} ${n.notes}`.slice(0, 500),
    redactionCode: note?.redactionCode || n?.redactionCode,
  }));
};

const insertRedactionCode = (
  slices: Array<AudioEditSlice>,
  payload: actions.AddRedactionCodeToTranscriptionRequest,
  user: string
) => {
  const { word, redactionCode } = payload;
  const index = slices.findIndex(
    ([s, e, _n]) => s <= word.startTimeMs && e >= word.stopTimeMs
  );
  const slice = slices[index];
  if (slice) {
    return replaceSliceAt(
      slices,
      index,
      replaceSliceNote(
        slice,
        slice[2]
          ? { ...slice[2], redactionCode }
          : {
              user,
              modifiedDateTime: new Date().toISOString(),
              labels: [],
              notes: '',
              hasAuditEvent: true,
              redactionCode,
            }
      )
    );
  }
  return slices;
};

const removeRedactionCode = (
  slices: Array<AudioEditSlice>,
  word: actions.RemoveRedactionCodeFromTranscriptionRequest
) => {
  const index = slices.findIndex(
    ([s, e, _n]) => s <= word.startTimeMs && e >= word.stopTimeMs
  );
  const slice = slices[index];
  if (slice) {
    return replaceSliceAt(
      slices,
      index,
      replaceSliceNote(slice, omit(slice[2], 'redactionCode'))
    );
  }
  return slices;
};

const replaceSliceAt = (
  arr: Array<AudioEditSlice>,
  index: number,
  value: AudioEditSlice
): Array<AudioEditSlice> => [
  ...arr.slice(0, index),
  value,
  ...arr.slice(index + 1),
];

const replaceSliceNote = (
  arr: AudioEditSlice,
  value: AudioEditDetails | null | undefined
): AudioEditSlice => [arr[0], arr[1], value];
